import React from 'react';
import { Title, Loader, VersionPopover } from '../../elements';
import { notifications, extractApiErrorMessage } from '../../utils';
import { Grid, Icon, Button, Tabs, Tab, Divider, Container, Chip } from '@material-ui/core';
import { LoaderSize } from '../../constants/constants';
import { ApiService } from '../../hooks';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { HealthSystemAPIControllersDeviceResponsesHealthInjectorDetailsResponse } from '../../api/HealthSystemApi';
import HealthInjectorEventsTable from '../../components/HealthInjectorEventsTable';
import HealthInjectorActivitiesTable from '../../components/HealthInjectorActivitiesTable';
import CreateUpdateHealthInjector from '../../components/CreateUpdateHealthInjector';
import { HealthInjectorVersion, HealthInjectorViewModel } from '../../models/device';

const HealthInjectorView: React.FC = () => {
  const apiService = new ApiService();
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const tabValues: { [key: string]: string } = {
    overview: 'overview',
    events: 'events',
    activities: 'activities'
  };

  const [currentTab, setCurrentTab] = React.useState(tabValues.overview);
  const [injector, setInjector] =
    React.useState<HealthSystemAPIControllersDeviceResponsesHealthInjectorDetailsResponse | null>(null);
  const [loaded, setLoaded] = React.useState(false);
  const [isBusy, setIsBusy] = React.useState(true);
  const [updateDialogOpen, setUpdateDialogOpen] = React.useState(false);

  const handleTabChange = (_event: React.ChangeEvent<any>, newValue: string) => {
    setCurrentTab(newValue);
  };

  const loadInjector = async () => {
    setIsBusy(true);
    setLoaded(false);

    if (!id) {
      notifications.error('Injector ID is undefined.');
      return;
    }

    await apiService.api.device
      .deviceGetHealthInjectorDetails({ id })
      .then((response) => {
        setInjector(response.data);
        setLoaded(true);
      })
      .catch((e: unknown) => {
        setLoaded(false);
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  React.useEffect(() => {
    if (loaded === false) {
      const loadData = async () => loadInjector();
      loadData();
    }
  }, [loaded]);

  const renderFarmStatus = () => {
    if (injector?.currentFarmId) {
      return (
        <Chip
          size="small"
          label={injector.currentFarmName || t('f-com-attached')}
          className="!bg-green-100 !text-green-800"
        />
      );
    }
    return <span className="text-gray-500">{t('f-farm-any')}</span>;
  };

  const renderDeviceStatus = () => {
    if (injector?.disabled) {
      return <Chip size="small" label={t('f-com-disabled')} className="!bg-red-100 !text-red-800" />;
    }
    if (injector?.hasErrors) {
      return <Chip size="small" label={t('f-com-has-errors')} className="!bg-amber-100 !text-amber-800" />;
    }
    return <Chip size="small" label={t('f-com-active')} className="!bg-green-100 !text-green-800" />;
  };

  const renderVersionInfo = () => {
    if (!injector?.version) return <span className="text-gray-400">-</span>;
    return (
      <VersionPopover
        version={injector.version as unknown as HealthInjectorVersion}
        id={`v${injector.macAddress}`}
        title={t('f-com-expand')}
      />
    );
  };

  const renderBooleanChip = (value: boolean | null | undefined, label?: string) => {
    if (value == null) return <span className="text-gray-400">-</span>;
    return (
      <Chip
        size="small"
        label={label || (value ? t('f-yes') : t('f-no'))}
        className={value ? '!bg-green-100 !text-green-800' : '!bg-gray-100 !text-gray-600'}
      />
    );
  };

  const convertToViewModel = (
    data: HealthSystemAPIControllersDeviceResponsesHealthInjectorDetailsResponse
  ): HealthInjectorViewModel => {
    return {
      id: data.id,
      macAddress: data.macAddress || '',
      comment: data.comment || '',
      created: new Date(data.createdAt),
      updated: data.updatedAt ? new Date(data.updatedAt) : new Date(),
      hasErrors: data.hasErrors,
      disabled: data.disabled,
      farm: data.currentFarmId
        ? {
            id: data.currentFarmId,
            number: data.currentFarmNumber || '',
            name: data.currentFarmName || ''
          }
        : null,
      version: data.version as unknown as HealthInjectorVersion,
      manufacturer: data.manufacturer || null
    };
  };

  const handleUpdateModalAction = (result: boolean | null) => {
    setUpdateDialogOpen(false);
    if (result === true) {
      setLoaded(false);
    }
  };

  const DetailRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0">
      <span className="text-gray-600 text-sm">{label}</span>
      <span className="font-medium text-gray-900">{children}</span>
    </div>
  );

  return (
    <Container maxWidth="lg">
      <Grid container spacing={2}>
        {/* Header with back button */}
        <Grid item xs={12}>
          <Grid container direction="row" alignItems="center" spacing={1}>
            <Grid item>
              <Button size="small" startIcon={<Icon>arrow_back</Icon>} onClick={() => navigate('/devices/injectors')}>
                {t('f-com-back')}
              </Button>
            </Grid>
          </Grid>
        </Grid>

        {/* Tabs section */}
        <Grid item xs={12}>
          <Grid container direction="column">
            <Tabs value={currentTab} onChange={handleTabChange} scrollButtons="auto">
              <Tab label={t('f-loc-view-overview')} value="overview" />
              <Tab label={t('f-logs-events')} value="events" />
              <Tab label={t('f-logs-activities')} value="activities" />
            </Tabs>
            <Divider />
          </Grid>
        </Grid>

        {/* Overview Tab Content */}
        {currentTab === tabValues.overview && (
          <Grid item xs={12}>
            <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />

            {loaded && injector && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Device Details Card */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Icon color="primary" fontSize="large">
                          nat
                        </Icon>
                        <Title>{t('f-settings-injector')}</Title>
                      </div>
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        startIcon={<Icon>edit</Icon>}
                        onClick={() => setUpdateDialogOpen(true)}
                      >
                        {t('f-com-update')}
                      </Button>
                    </div>
                  </div>
                  <div className="p-6">
                    <DetailRow label={t('f-logs-mac')}>{injector.macAddress}</DetailRow>
                    <DetailRow label={t('f-com-farm')}>{renderFarmStatus()}</DetailRow>
                    <DetailRow label={t('f-com-status')}>{renderDeviceStatus()}</DetailRow>
                    <DetailRow label={t('f-com-created')}>{new Date(injector.createdAt).toLocaleString()}</DetailRow>
                    <DetailRow label={t('f-com-updated')}>
                      {injector.updatedAt ? new Date(injector.updatedAt).toLocaleString() : '-'}
                    </DetailRow>
                    <DetailRow label={t('f-com-last-connection')}>
                      {injector.lastConnectionAt ? new Date(injector.lastConnectionAt).toLocaleString() : '-'}
                    </DetailRow>
                    <DetailRow label={t('f-com-signal-strength')}>
                      {injector.manufacturer?.rssiDbm != null ? `${injector.manufacturer.rssiDbm} dBm` : '-'}
                    </DetailRow>
                    <DetailRow label={t('f-device-ver')}>{renderVersionInfo()}</DetailRow>
                    <DetailRow label={t('m-global-comments')}>
                      {injector.comment || <span className="text-gray-400">-</span>}
                    </DetailRow>
                  </div>
                </div>

                {/* Manufacturer Card */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  <div className="bg-blue-50 px-6 py-4 border-b border-blue-100">
                    <div className="flex items-center gap-3">
                      <Icon className="!text-blue-600" fontSize="large">
                        precision_manufacturing
                      </Icon>
                      <h2 className="text-lg font-semibold text-blue-900">{t('f-injector-manufacturer')}</h2>
                    </div>
                  </div>
                  <div className="p-6">
                    <DetailRow label={t('f-injector-model')}>
                      {injector.manufacturer?.model || <span className="text-gray-400">-</span>}
                    </DetailRow>
                    <DetailRow label={t('f-injector-motor-type')}>
                      {injector.manufacturer?.motorType || <span className="text-gray-400">-</span>}
                    </DetailRow>
                    <DetailRow label={t('f-injector-description')}>
                      {injector.manufacturer?.description || <span className="text-gray-400">-</span>}
                    </DetailRow>

                    <DetailRow label={t('f-injector-pcb-type')}>
                      {injector.manufacturer?.pcbType || <span className="text-gray-400">-</span>}
                    </DetailRow>

                    <div className="mt-6 pt-4 border-t border-gray-200">
                      <h3 className="text-sm font-medium text-gray-700 mb-4">{t('f-injector-features')}</h3>
                      <div className="flex flex-wrap gap-3">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600">{t('f-injector-aluminum-coated')}:</span>
                          {renderBooleanChip(injector.manufacturer?.aluminumCoated)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Grid>
        )}

        {/* Events Tab Content */}
        {currentTab === tabValues.events && id && (
          <Grid item xs={12}>
            <HealthInjectorEventsTable injectorId={id} />
          </Grid>
        )}

        {/* Activities Tab Content */}
        {currentTab === tabValues.activities && id && (
          <Grid item xs={12}>
            <HealthInjectorActivitiesTable injectorId={id} />
          </Grid>
        )}
      </Grid>

      {/* Update Dialog */}
      {injector && (
        <CreateUpdateHealthInjector
          opened={updateDialogOpen}
          onModalAction={handleUpdateModalAction}
          mode="update"
          injector={convertToViewModel(injector)}
        />
      )}
    </Container>
  );
};

export default HealthInjectorView;
